// Libs
import Compressor from 'compressorjs';
import { toast } from 'react-toastify';
import { TFunction } from 'i18next';
import moment from 'moment';
import 'moment-duration-format';
// Others
import {
  DEFAULT_DECIMAL_POINT,
  DEFAULT_MAX_HEIGHT_IMAGE,
  DEFAULT_MAX_WIDTH_IMAGE,
  DEFAULT_QUALITY_IMAGE,
  EMPTY_STRING,
  IMAGE_MIME_TYPES,
  RANDOM_RANGE,
  REG_EXP,
  roleDetailOrderRouteMap,
  TIME_CLOSE_TOAST,
  TIME_OUT_SHOW_TOAST_ERROR,
  TIME_OUT_SHOW_TOAST_SUCCESS,
} from '~/utils/constants/common';
import { IBaseUser, IRangeDate } from '~/utils/interface/common';
import { DateFormatEnum, DiscountTypeEnum, RoleEnum, ToastTypeEnum } from '~/utils/enum/common';
import { NavigateToOrderDetailParams } from '~/utils/interface/order';

/**
 * This handle get data time zone
 * @returns data time zone
 */
export const dataTimeZone = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

export const formatNumberShort = (num: number): string => {
  if (num >= 1_000_000) return `${Math.floor(num / 1_000_000)}M`;
  if (num >= 1_000) return `${Math.floor(num / 1_000)}k`;
  return `${num}`;
};

/**
 * Function to get the start and end date of the current week.
 * @returns An object containing the start and end date of the current week.
 */
export const getCurrentWeekRange = () => {
  const startDate = moment().startOf('week').format('YYYY-MM-DD');
  const endDate = moment().endOf('week').format('YYYY-MM-DD');

  const rangeDate: IRangeDate = {
    startDate,
    endDate,
  };

  return rangeDate;
};

/**
 * Function to get the number of days in a given month of a year.
 * @param year The year (e.g., 2025).
 * @param month The month (1-12).
 * @returns The total number of days in the specified month.
 */
export const getDaysInMonth = (year: number, month: number): number => {
  return moment(`${year}-${month}`, 'YYYY-MM').daysInMonth();
};

/**
 * Get the difference between two times in minutes.
 * @param startTime The start time.
 * @param endTime The end time.
 * @returns The difference between the two times in minutes.
 */
export const getDifferenceTime = (startTime: string, endTime: string): number => {
  const start = moment(startTime);
  const end = moment(endTime);

  return end.diff(start, 'minutes');
};

/**
 * Format ID.
 * @param id ID to format.
 * @returns Formatted ID string.
 */
export const formatID = (id: string): string => {
  return id.toString().padStart(3, '0');
};

/**
 * Convert seconds to minutes.
 * @param seconds Seconds to convert.
 * @returns Minutes.
 */
export const convertSecondsToMinutes = (seconds?: number): number => {
  if (!seconds) return 0;

  return Math.floor(seconds / 60);
};

/**
 * Converts a date string to a relative time string (e.g., "30 minutes ago").
 * @param {string | Date} date - The date string to convert.
 * @returns {string} - The relative time string.
 */
export const convertToRelativeTime = (date: string | Date): string => {
  return moment(date).fromNow();
};

/**
 * Format currency to USD.
 * @param str Number string to format.
 * @returns Formatted currency string.
 */
export const formatCurrencyUSD = ({
  value,
  showDecimal = false,
  useGrouping = true,
}: {
  value?: string | number;
  showDecimal?: boolean;
  useGrouping?: boolean;
}): string => {
  const valueCurrency = value?.toString();

  if (!valueCurrency) {
    return !showDecimal ? '$0' : '$0.00';
  }

  const number = Number(valueCurrency);
  if (isNaN(number)) {
    return !showDecimal ? '$0' : '$0.00';
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    useGrouping: useGrouping,
    minimumFractionDigits: !showDecimal ? 0 : 2,
    maximumFractionDigits: !showDecimal ? 0 : 2,
  }).format(number);
};

/**
 * Function to get the full name of a user by concatenating the first name, middle name, and last name.
 * @param user - The user object containing firstName, middleName, and lastName.
 * @returns A string representing the full name of the user, or an empty string if no name components are provided.
 */
export const getFullName = (user: IBaseUser) => {
  const { firstName, middleName, lastName } = user;

  const nameParts = [firstName?.trim(), middleName?.trim(), lastName?.trim()].filter(Boolean);

  if (nameParts.length === 0) {
    return EMPTY_STRING;
  }

  return nameParts.join(' ');
};

/**
 * Function to get the start and end date of a month.
 * @param year - The year of the month.
 * @param month - The month number (1-12).
 * @returns An object containing the start and end date of the month.
 */
export const getMonthRange = (year: number, month: number) => {
  const startDate = moment({ year, month }).startOf('month').format('YYYY-MM-DD');
  const endDate = moment({ year, month }).endOf('month').format('YYYY-MM-DD');
  return { startDate, endDate };
};

/**
 * Generates an avatar name abbreviation from a full name.
 * @param name The full name as a string.
 * @returns A string containing the first letter of the first and last name.
 */
export const getAvatarName = (name: string) => {
  if (!name) return EMPTY_STRING;

  return `${name.split(' ')[0][0]}${name.split(' ')[1][0]}`;
};

/**
 * Function to get the initials from a person's full name.
 * @param params - An object containing the full name.
 * @returns A string containing the initials formed by combining the first letter of the first name and the first letter of the last name.
 */
export const getInitials = (fullName?: string): string => {
  if (!fullName) return EMPTY_STRING;

  const names = fullName.trim().split(/\s+/);
  const firstInitial = names[0]?.charAt(0).toUpperCase() || EMPTY_STRING;
  const lastInitial = names[1]?.charAt(0).toUpperCase() || EMPTY_STRING;

  return firstInitial + lastInitial;
};

/**
 * Convert uri to name file without extension.
 * @param uri is uri of file.
 * @returns name file without extension.
 */
export const getFileNameFromUri = (uri: string): string => {
  return (
    uri
      .split('/')
      .pop()
      ?.replace(/^\d+_/, '')
      .replace(/\.[^/.]+$/, '') || EMPTY_STRING
  );
};

/**
 * Convert uri to type of file.
 * @param uri is uri of file.
 * @returns data type of file.
 */
export const getTypeFileFromUri = (uri: string): string => {
  return uri?.split('.')?.pop() || EMPTY_STRING;
};

/**
 * Format file size from bytes to KB or MB.
 * @param bytes The file size in bytes.
 * @returns A string size file
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 KB';

  const KB = 1024;
  const MB = KB * 1024;

  if (bytes >= MB) {
    return `${(bytes / MB).toFixed(1)} MB`;
  }

  return `${(bytes / KB).toFixed(1)} KB`;
};

/**
 * Formats the input string into a valid currency format.
 *
 * This function processes a string input to ensure it conforms to a valid
 * currency format, allowing only digits and a single decimal point. It
 * also ensures that:
 * - Only one leading zero is retained before the decimal point.
 * - The decimal part can contain a maximum of two digits.
 * - If the input starts with a decimal point, it returns an empty string.
 *
 * @param value The string value that needs to be formatted as currency.
 * @returns A formatted string that represents a valid currency value,
 *          or an empty string if the input starts with a decimal point.
 */
export const formatInputCurrency = (
  value: string,
  allowNegative?: boolean,
  decimal: number = DEFAULT_DECIMAL_POINT
) => {
  if (value.startsWith('.')) {
    return EMPTY_STRING;
  }

  let inputValue = value;

  let isNegative = false;
  if (allowNegative && inputValue.startsWith('-')) {
    isNegative = true;
    inputValue = inputValue.slice(1);
  }

  if (isNegative && inputValue.startsWith('.')) {
    return EMPTY_STRING;
  }

  inputValue = inputValue.replace(/[^\d.]/g, EMPTY_STRING);

  const parts = inputValue.split('.');
  if (parts[0].length > 1) {
    parts[0] = parts[0].replace(/^0+/, EMPTY_STRING);
    if (parts[0] === EMPTY_STRING) {
      parts[0] = '0';
    }
  }

  inputValue = parts.join('.');

  const decimalCount = (inputValue.match(/\./g) || []).length;
  if (decimalCount > 1) {
    return (isNegative ? '-' : EMPTY_STRING) + inputValue.slice(0, -1);
  }

  if (parts.length > 1 && parts[1].length > decimal) {
    return (isNegative ? '-' : EMPTY_STRING) + `${parts[0]}.${parts[1].slice(0, decimal)}`;
  }

  return (isNegative ? '-' : EMPTY_STRING) + inputValue;
};

/**
 * Converts seconds into a short duration format.
 * @param seconds Duration in seconds.
 * @returns A string in the format of d, h, m, or s.
 */
export const formatDurationShort = (seconds: number): string => {
  const duration = moment.duration(seconds, 'seconds');

  if (duration.asDays() >= 1) {
    return `${Math.floor(duration.asDays())}d`;
  } else if (duration.asHours() >= 1) {
    return `${Math.floor(duration.asHours())}h`;
  } else if (duration.asMinutes() >= 1) {
    return `${Math.floor(duration.asMinutes())}m`;
  } else {
    return `${Math.floor(duration.asSeconds())}s`;
  }
};

/**
 * Function to format phone numbers in the form (xxx) xxx-xxx.
 * @param value is the value used for formatting.
 * @returns phone number in format.
 */
export const formatPhoneNumber = (value?: string): string => {
  if (!value) return EMPTY_STRING;

  const phoneNumber = value.replace(REG_EXP.NUMBER, EMPTY_STRING);
  const phoneNumberLength = phoneNumber.length;

  if (phoneNumberLength < 4) return phoneNumber;

  if (phoneNumberLength < 7) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  }

  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
};

/**
 * Function to remove all non-digit characters from a given string.
 * @param value - The string value that needs to be cleaned of non-digit characters.
 * @returns A string containing only the digits from the value.
 */
export const removeNonDigits = (value?: string) => {
  if (!value) return EMPTY_STRING;

  return value.replace(REG_EXP.NON_DIGIT_REGEX, EMPTY_STRING);
};

export const getImageMimeType = (fileName: string): string | null => {
  const ext = fileName.split('?')[0].split('.').pop()?.toLowerCase();

  if (!ext) return null;

  for (const [mime, exts] of Object.entries(IMAGE_MIME_TYPES)) {
    if (exts.includes(ext)) {
      return mime;
    }
  }

  return null;
};

/**
 * Date format.
 * @param date is the time series that needs formatting.
 * @param format the date type you want to format.
 * @returns string after formatting.
 */
export const formatDate = (date: string | Date | undefined, format?: DateFormatEnum): string => {
  if (!date) return EMPTY_STRING;

  if (typeof date === 'string' && /^\d{2}:\d{2}$/.test(date)) {
    const today = moment().format(DateFormatEnum.YYYY_MM_DD);
    date = `${today}T${date}`;
  }

  const m = moment(date);
  if (!m.isValid()) return EMPTY_STRING;

  return m.format(format || DateFormatEnum.YYYY_MM_DD);
};

export const formatTimeTo12h = (time24?: string): string => {
  if (!time24) return EMPTY_STRING;

  return moment(time24, DateFormatEnum.HH_MM_SS).format(DateFormatEnum.HH_mm_A);
};

export const convertTimeToHHmm = (time?: string): string => {
  if (!time) return EMPTY_STRING;

  return moment(time, 'HH:mm:ss').format('HH:mm');
};

/**
 * Function to remove empty fields from an object.
 * @param data is the object that needs to be cleaned.
 * @returns an object without empty fields.
 */
export const cleanEmptyFields = <T>(data: T): T => {
  if (data === null || data === undefined || data === '') {
    return undefined as unknown as T;
  }

  if (Array.isArray(data)) {
    return data.map((item) => cleanEmptyFields(item)).filter((item) => item !== undefined) as unknown as T;
  }

  if (typeof data === 'object') {
    const cleanedObj: Record<string, any> = {};
    Object.entries(data).forEach(([key, value]) => {
      const cleanedValue = cleanEmptyFields(value);
      if (cleanedValue !== undefined) {
        cleanedObj[key] = cleanedValue;
      }
    });
    return cleanedObj as T;
  }

  return data;
};

export const cleanObject = <T extends Record<string, any>>(
  obj?: T,
  options: {
    removeKeys?: string[];
    removeNull?: boolean;
    removeEmptyStrings?: boolean;
  } = {}
): Partial<T> => {
  if (!obj) return {};

  const { removeKeys = [], removeNull = true, removeEmptyStrings = false } = options;

  return Object.fromEntries(
    Object.entries(obj).filter(([key, value]) => {
      if (removeKeys.includes(key)) return false;
      if (removeNull && value === null) return false;
      if (removeEmptyStrings && value === '') return false;
      return true;
    })
  ) as Partial<T>;
};

/**
 * Converts a string to title case.
 * @param str - The string to be converted.
 * @returns The converted string.
 */
export const formatTitleCase = (str?: string): string => {
  if (!str) return EMPTY_STRING;

  const newStr = str
    .replace(/([A-Z])/g, ' $1')
    .toLowerCase()
    .replace(/_/g, ' ')
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  return newStr;
};

/**
 * Converts a string to parse html text.
 * @param str - The string to be converted.
 * @returns The converted string.
 */
export const getParseHtmlText = (htmlString: string) => {
  if (!htmlString) return { text: EMPTY_STRING, url: EMPTY_STRING };

  const isHtml = /<[a-z][\s\S]*>/i.test(htmlString);

  if (!isHtml) {
    return { text: htmlString, url: EMPTY_STRING };
  }

  const urlRegex = /href\s*=\s*['"]([^'"]+)['"]/i;
  const urlMatch = htmlString.match(urlRegex);

  const textContent = htmlString.replace(/<[^>]*>/g, '').trim();

  return {
    url: urlMatch ? urlMatch[1] : EMPTY_STRING,
    text: textContent || htmlString,
  };
};

/**
 * Generates a unique identifier string.
 * @param randomRange The range within which the random number is generated. Default is 1000.
 * @returns A unique identifier string composed of the current timestamp and a random number.
 */
export const generateUniqueId = (randomRange: number = RANDOM_RANGE): string => {
  return `${Date.now()}-${Math.floor(Math.random() * randomRange)}`;
};

/**
 * Generates a list of formatted date strings between two given dates (inclusive).
 * @param start The start date as a string (parsable by moment).
 * @param end The end date as a string (parsable by moment).
 * @returns An array of formatted date strings in the specified format (e.g., MM/DD/YYYY).
 */
export const listDatesBetween = (start: string, end: string) => {
  const startDate = moment(start);
  const endDate = moment(end);
  const dateList: string[] = [];

  const current = startDate.clone();
  while (current.isSameOrBefore(endDate)) {
    dateList.push(current.format(DateFormatEnum.MM_DD_YYYY));
    current.add(1, 'day');
  }

  return dateList;
};

/**
 * Validate value with regex.
 * @param value is the validate value.
 * @param regex is the regex used to check.
 * @returns true if regex is satisfied and vice versa.
 */
export const validateWithRegex = (value: string, regex: RegExp): boolean => {
  return regex.test(value);
};

export const generateGuid = (): string => {
  const generateSegment = () => {
    return Math.floor((1 + Math.random()) * 0x10000)
      .toString(16)
      .substring(1);
  };

  const guid =
    generateSegment() +
    generateSegment() +
    '-' +
    generateSegment() +
    '-' +
    generateSegment() +
    '-' +
    generateSegment() +
    '-' +
    generateSegment() +
    generateSegment() +
    generateSegment();

  return guid;
};

/**
 * Handle show toast message.
 * @param type is option of reducer.
 * @param message is option of reducer.
 * @returns modal toast by status
 */
export const customToast = (type: ToastTypeEnum, message: string) => {
  switch (type) {
    case ToastTypeEnum.ERROR:
      setTimeout(() => {
        toast.error(message, {
          autoClose: TIME_CLOSE_TOAST,
        });
      }, TIME_OUT_SHOW_TOAST_ERROR);
      break;
    case ToastTypeEnum.SUCCESS:
      setTimeout(() => {
        toast.success(message, {
          autoClose: TIME_CLOSE_TOAST,
        });
      }, TIME_OUT_SHOW_TOAST_SUCCESS);
      break;
    case ToastTypeEnum.WARNING:
      toast.warning(message, {
        autoClose: TIME_CLOSE_TOAST,
      });
      break;
    default:
      toast(message, {
        autoClose: TIME_CLOSE_TOAST,
      });
      break;
  }
};

/**
 * Function to compress an image file using the Compressor library.
 * @param file - The image file to be compressed.
 * @param quality - The quality of the compressed image file.
 * @returns A promise that resolves to the compressed image file or blob.
 */
export const compressedImage = async (file: File, quality: number = DEFAULT_QUALITY_IMAGE) => {
  const compressedFile = await new Promise<File | Blob>((resolve, reject) => {
    new Compressor(file, {
      quality,
      maxWidth: DEFAULT_MAX_WIDTH_IMAGE,
      maxHeight: DEFAULT_MAX_HEIGHT_IMAGE,
      success(result) {
        const newFile: File = new File([result], file.name, { type: file.type });
        resolve(newFile);
      },
      error(error) {
        reject(error);
      },
    });
  });

  return compressedFile;
};

/**
 * Creates a FormData object from the provided data, compressing image files if needed.
 * @param {Record<string, any>} data - The data object
 * @param {array} imageKeys - The key in the data object that contains the image file to be compressed.
 * @param quality is image quality
 * @returns {Promise<FormData>}
 */
export const createFormData = async (
  data: Record<string, any>,
  imageKeys: string[] = [],
  quality: number = DEFAULT_QUALITY_IMAGE
): Promise<FormData> => {
  const formData = new FormData();

  for (const [key, value] of Object.entries(data)) {
    if (value === undefined || value === null) continue;

    if (imageKeys?.includes(key) && value?.file && typeof value === 'object' && value?.file?.size > 0) {
      if (value.file) {
        const compressedFile = await compressedImage(value?.file, quality);

        formData.append(key, compressedFile);
      }
    } else if (imageKeys?.includes(key) && Array.isArray(value)) {
      for (const file of value) {
        if (file?.file && file?.file?.type?.includes('image') && file?.file?.size > 0) {
          const compressedFile = await compressedImage(file?.file, quality);

          formData.append(key, compressedFile);
        } else {
          formData.append(key, file?.file);
        }
      }
    } else if (Array.isArray(value) || typeof value === 'object') {
      formData.append(key, JSON.stringify(value));
    } else {
      formData.append(key, value as string);
    }
  }

  return formData;
};

/**
 * Remove empty objects from an object.
 * @param obj - The object to remove empty objects from.
 * @returns The object with empty objects removed.
 */
export const removeEmptyObjects = (obj: Record<string, any> | null | undefined): Record<string, any> => {
  // Handle null/undefined or non-object inputs
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
    return {};
  }

  return Object.entries(obj).reduce((acc, [key, value]) => {
    // Helper to check if a value is considered empty
    const isValueEmpty = (val: any): boolean => {
      if (val === null || val === undefined) return true;
      if (typeof val === 'string' && val.trim() === EMPTY_STRING) return true;
      if (typeof val === 'object' && !Array.isArray(val)) {
        return Object.keys(removeEmptyObjects(val)).length === 0;
      }
      return false;
    };

    // Check if current value should be removed
    if (isValueEmpty(value)) {
      return acc;
    }

    // Process arrays
    if (Array.isArray(value)) {
      const cleanedArray = value
        .map((item) => {
          if (typeof item === 'object' && item !== null) {
            // Preserve objects with 'file' key (special case)
            if ('file' in item) return item;
            return removeEmptyObjects(item);
          }
          return item;
        })
        .filter((item) => !isValueEmpty(item)); // Remove empty items

      if (cleanedArray.length > 0) {
        acc[key] = cleanedArray;
      }
      return acc;
    }

    // Process plain objects (excluding special objects like Date)
    if (typeof value === 'object' && Object.prototype.toString.call(value) === '[object Object]') {
      if ('file' in value) {
        acc[key] = value;
        return acc;
      }

      const cleanedValue = removeEmptyObjects(value);
      if (Object.keys(cleanedValue).length > 0) {
        acc[key] = cleanedValue;
      }
      return acc;
    }

    // Preserve other values (numbers, booleans, non-empty strings, etc.)
    acc[key] = value;
    return acc;
  }, {} as Record<string, any>);
};

/**
 * Function to check if an object is empty.
 * @param obj The object to check.
 * @returns True if the object is empty, false otherwise.
 */
export const isEmptyObject = (obj?: object) => {
  if (!obj) return true;

  return Object.keys(obj).length === 0;
};

/**
 * Compares two objects and returns a partial object containing the updated values.
 * @param originalData - The original data object.
 * @param editedData - The edited data object.
 * @returns An object containing keys from editedData with values that differ from originalData.
 */
export const compareDataUpdate = (
  originalData: { [key: string]: any },
  editedData: { [key: string]: any }
): { [key: string]: any } => {
  const result: { [key: string]: any } = {};

  Object.entries(originalData).forEach(([key, value]) => {
    if (Array.isArray(value) && Array.isArray(editedData[key])) {
      const changedArray = editedData[key].reduce((acc: any[], editedItem: any, index: number) => {
        const originalItem = value[index];

        if (!originalItem || JSON.stringify(originalItem) !== JSON.stringify(editedItem)) {
          acc.push(editedItem);
        }
        return acc;
      }, [] as any[]);

      if (changedArray.length > 0) {
        result[key] = changedArray;
      }
    } else if (JSON.stringify(value) !== JSON.stringify(editedData[key])) {
      result[key] = editedData[key];
    }
  });

  Object.keys(editedData).forEach((key) => {
    if (!(key in originalData)) {
      result[key] = editedData[key];
    }
  });
  return result;
};

/**
 * Returns a subset of the data object containing only the fields that have been marked as changed.
 * This function extracts the fields from the `data` object whose corresponding keys are present in the `dirtyFields` object.
 *
 * @template T - The type of the data object.
 * @param {Partial<Record<keyof T, any>>} dirtyFields - An object indicating which fields have been modified.
 * The keys are the fields in the `data` object, and the values indicate whether a field has been changed.
 * @param {T} data - The original data object from which to extract the changed fields.
 * @returns {Partial<T>} - A new object containing only the fields from the `data` object that were marked as changed in the `dirtyFields` object.
 */
export const getChangedDataForm = <T extends Record<string, any>>(
  dirtyFields: Partial<Record<keyof T, any>>,
  data: T
): Partial<T> => {
  const result: Partial<T> = {};

  for (const key in dirtyFields) {
    const dirtyValue = dirtyFields[key];
    const dataValue = data[key];

    if (Array.isArray(dirtyValue)) {
      const hasAnyChanged = dirtyValue.some((item: any) => {
        if (item === true) return true;
        if (typeof item === 'object' && item !== null) {
          return Object.values(item).some((v) => v === true);
        }
        return false;
      });

      if (hasAnyChanged) {
        result[key] = dataValue;
      }
    } else if (typeof dirtyValue === 'object' && dirtyValue !== null) {
      const nestedChanged = getChangedDataForm(dirtyValue, dataValue);
      if (Object.keys(nestedChanged).length > 0) {
        result[key] = nestedChanged as T[typeof key];
      }
    } else if (dirtyValue === true) {
      result[key] = dataValue;
    }
  }

  return result;
};

/**
 * Generates a file name with a timestamp.
 *
 * @param fileName The base file name.
 * @returns The generated file name.
 */
export const handleGenerateNameFileExcel = (fileName: string) => {
  const name = `${fileName}_${moment().format(DateFormatEnum.YYYYMMDD_HHmmss)}.csv`;

  return name;
};

/**
 * Generates an array of dates between two given dates (inclusive).
 *
 * @param startDate The start date.
 * @param endDate The end date.
 * @returns An array of dates in YYYY-MM-DD format.
 */
export const handleDaysBetween = (startDate: string, endDate: string): string[] => {
  const result: string[] = [];
  const start = moment(startDate);
  const end = moment(endDate);

  while (start.isSameOrBefore(end)) {
    result.push(start.format(DateFormatEnum.YYYY_MM_DD));
    start.add(1, 'day');
  }

  return result;
};

/**
 * Capitalizes the first letter of each word in a string.
 *
 * @param str The string to capitalize.
 * @returns The capitalized string.
 */
export const capitalizeWords = (str: string) => str.replace(/\b\w/g, (char) => char.toUpperCase());

/**
 * Get the role name corresponding to a given role.
 * @param roleId - The role to look up.
 * @returns The display name of the role, or an empty string if not found.
 */
export const getRoleName = (roleId: string, t: TFunction): string => {
  if (!roleId) return EMPTY_STRING;

  switch (roleId) {
    case RoleEnum.SUPER_ADMIN:
      return t('role_title_super_admin', { ns: 'admin/role' });
    case RoleEnum.SALES_ADMIN:
      return t('role_title_sales_admin', { ns: 'admin/role' });
    case RoleEnum.SALES_PERSON:
      return t('role_title_sales_person', { ns: 'admin/role' });
    case RoleEnum.OPERATIONS_MANAGER:
      return t('role_title_operations_manager', { ns: 'admin/role' });
    case RoleEnum.WAREHOUSE_LEAD:
      return t('role_title_warehouse_lead', { ns: 'admin/role' });
    case RoleEnum.WAREHOUSE_STAFF:
      return t('role_title_warehouse_staff', { ns: 'admin/role' });
    case RoleEnum.FOREMAN:
      return t('role_title_foreman', { ns: 'admin/role' });
    case RoleEnum.CREW:
      return t('role_title_crew', { ns: 'admin/role' });
    case RoleEnum.DRIVER:
      return t('role_title_driver', { ns: 'admin/role' });
    case RoleEnum.LOADER:
      return t('role_title_loader', { ns: 'admin/role' });
    default:
      return EMPTY_STRING;
  }
};

export const formatPercent = (value: string): string => {
  if (!value) return EMPTY_STRING;

  const num = Number(value);

  if (isNaN(num)) return '0%';

  return `${num.toFixed(0)}%`;
};

export const formatDurationTime = (minutes: number): string => {
  const duration = moment.duration(minutes, 'minutes');

  if (duration.asDays() >= 1) {
    return `${Math.floor(duration.asDays())}d`;
  }
  if (duration.asHours() >= 1) {
    return `${Math.floor(duration.asHours())}h`;
  }

  return `${Math.floor(duration.asMinutes())}m`;
};

export const getDatesInRange = (start: string, end: string) => {
  const dates: string[] = [];
  let current = moment(start);

  while (current.isSameOrBefore(end, 'day')) {
    dates.push(current.format('YYYY-MM-DD'));
    current.add(1, 'day');
  }

  return dates;
};

export const navigateToOrderDetailByRole = ({ navigate, role, orderId }: NavigateToOrderDetailParams) => {
  const routeBuilder = roleDetailOrderRouteMap[role];

  if (!routeBuilder || !orderId) {
    console.warn('Invalid role or orderId', { role, orderId });
    return;
  }

  navigate(routeBuilder(orderId));
};

export const handleCalculateDiscountedPrice = (price: number, discountRate: number, discountTypeProduct: string) => {
  const priceData = price || 0;
  const discountRateData = discountRate || 0;

  if (discountTypeProduct === DiscountTypeEnum.AMOUNT) {
    const discounted = priceData - discountRateData;
    return Math.max(0, discounted);
  } else {
    const discounted = (priceData * (100 - discountRateData)) / 100;
    return Math.max(0, discounted);
  }
};
